import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { site } from "../content/site";
import { useContent, KMarkV2, SlotImg } from "../ui/helpers";

/*
  HEADER — floating glass "pill" with a Platform mega menu (v2 design).
  Text and links: src/site/content/site.js. Styles: site.css (.hdr, .k-nav, .mega, .drawer).
  Accessibility: skip link, Escape closes menus, mobile menu traps focus and returns it.
*/
const href = (l) => (l.hash ? `${l.to}#${l.hash}` : l.to);

/** K mark + wordmark, used in header and footer. */
export function Logo({ pulse = false }) {
  return (
    <span className="brand-in">
      <span className="brand-badge"><KMarkV2 pulse={pulse} /></span>
      <span className="logo-word">K SMART SYSTEMS</span>
    </span>
  );
}

function LangSwitch({ label }) {
  const { lang, setLang } = useLang();
  return (
    <div className={`k-lang${lang === "ko" ? " kr" : ""}`} role="group" aria-label={label}>
      <button type="button" lang="en" aria-pressed={lang === "en"} onClick={() => setLang("en")}>EN</button>
      <button type="button" lang="ko" aria-pressed={lang === "ko"} onClick={() => setLang("ko")}>KR</button>
    </div>
  );
}

export function SiteHeader() {
  const c = useContent(site);
  const { pathname, hash } = useLocation();
  const [solid, setSolid] = useState(false);
  const [mega, setMega] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const ref = useRef(null);
  const menuBtn = useRef(null);
  const drawerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMega(false); setDrawer(false); }, [pathname, hash]);

  useEffect(() => {
    const key = (e) => {
      if (e.key !== "Escape") return;
      if (drawer) { setDrawer(false); menuBtn.current?.focus(); }
      setMega(false);
    };
    const click = (e) => { if (ref.current && !ref.current.contains(e.target)) setMega(false); };
    document.addEventListener("keydown", key);
    document.addEventListener("click", click);
    return () => { document.removeEventListener("keydown", key); document.removeEventListener("click", click); };
  }, [drawer]);

  // mobile drawer: lock page scroll, move focus in, keep Tab inside
  useEffect(() => {
    if (!drawer) { document.body.style.overflow = ""; return undefined; }
    document.body.style.overflow = "hidden";
    const el = drawerRef.current;
    el?.querySelector("a,button")?.focus();
    const trap = (e) => {
      if (e.key !== "Tab" || !el) return;
      const f = [...el.querySelectorAll("a,button")];
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    };
    document.addEventListener("keydown", trap);
    return () => { document.removeEventListener("keydown", trap); document.body.style.overflow = ""; };
  }, [drawer]);

  const [col1, col2] = [c.platformMenu.slice(0, 2), c.platformMenu.slice(2)];
  const MegaCol = ({ title, links }) => (
    <div>
      <h4>{title}</h4>
      <ul>{links.map((l) => <li key={l.title}><Link to={href(l)} onClick={() => setMega(false)}><strong>{l.title}</strong><span>{l.text}</span></Link></li>)}</ul>
    </div>
  );

  return (
    <div className="kss-v2" ref={ref}>
      <a className="skip" href="#main">{c.skip}</a>
      <header className={`hdr${solid || mega ? " solid" : ""}`} data-testid="site-header">
        <div className="hdr-pill">
          <Link className="brand" to="/" aria-label={c.homeLabel}><Logo pulse /></Link>

          <ul className="k-nav">
            <li>
              <button type="button" aria-expanded={mega} aria-controls="kss-mega" onClick={() => setMega((v) => !v)}
                className={pathname.startsWith("/platform") ? "cur" : undefined}>
                {c.platform}
                <svg width="10" height="6" viewBox="0 0 10 6" aria-hidden="true"><path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.6" fill="none" /></svg>
              </button>
            </li>
            {c.nav.map((n) => (
              <li key={n.to}><NavLink to={n.to} className={({ isActive }) => (isActive ? "cur" : undefined)}>{n.label}</NavLink></li>
            ))}
          </ul>

          <div className="hdr-right">
            <LangSwitch label={c.language} />
            <Link className="k-btn k-btn-mint k-btn-demo" to="/contact">{c.contact} <i aria-hidden="true">→</i></Link>
            <button ref={menuBtn} type="button" className="menu-btn" aria-label={c.openMenu} aria-expanded={drawer} aria-controls="mobile-menu" onClick={() => setDrawer(true)}>
              <svg width="16" height="11" viewBox="0 0 16 11" aria-hidden="true"><path d="M0 1h16M0 5.5h16M0 10h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
            </button>
          </div>
        </div>

        {mega && (
          <div className="mega" id="kss-mega">
            <div className="mega-in">
              <MegaCol title={c.mega.col1} links={col1} />
              <MegaCol title={c.mega.col2} links={col2} />
              <Link className="mega-feature" to={c.mega.feature.to} onClick={() => setMega(false)}>
                <SlotImg slot={c.mega.feature.media} decorative sizes="360px" />
                <div><strong>{c.mega.feature.title}</strong><span>{c.mega.feature.text} →</span></div>
              </Link>
            </div>
          </div>
        )}
      </header>

      {drawer && (
        <div className="drawer" id="mobile-menu" role="dialog" aria-modal="true" aria-label={c.menu} ref={drawerRef}>
          <div className="drawer-top">
            <Link className="brand" to="/" aria-label={c.homeLabel}><Logo /></Link>
            <button type="button" className="menu-btn" style={{ display: "inline-grid" }} aria-label={c.closeMenu} onClick={() => { setDrawer(false); menuBtn.current?.focus(); }}>✕</button>
          </div>
          <nav aria-label={c.menu}>
            <Link to="/platform">{c.platform}</Link>
            {c.nav.map((n) => <Link key={n.to} to={n.to}>{n.label}</Link>)}
            <Link to="/demo#twin" className="drawer-sub">{c.platformMenu[2].title}</Link>
          </nav>
          <div className="drawer-foot">
            <LangSwitch label={c.language} />
            <Link className="k-btn k-btn-mint" to="/contact">{c.contact} <span className="ar" aria-hidden="true">→</span></Link>
          </div>
        </div>
      )}
    </div>
  );
}
